import { useRef, useEffect, useState } from 'react';
import PalmerSectionLabel from './PalmerSectionLabel';

const articles = [
  {
    slug: 'gregory-lalle',
    title: 'Gregory Lalle',
    date: 'May 21, 2024',
    excerpt: 'Good design is not just about structure—it\'s about the emotional weight of space, rhythm, and silence.',
    mainImg: 'https://framerusercontent.com/images/7uG4BhwVaiwETVmXbIX3b81RuRw.png',
    authorImg: 'https://framerusercontent.com/images/Ma6QjV4s6M3aDB1yx2zZTsvfw.png',
  },
  {
    slug: 'clive-willow',
    title: 'Clive Willow',
    date: 'February 5, 2024',
    excerpt: 'Typography today is no longer static. It breathes, scales, and adapts—blurring the lines between function and expression.',
    mainImg: 'https://framerusercontent.com/images/X8dp4VYqMPiYUc91IS8zJtaSg.png',
    authorImg: 'https://framerusercontent.com/images/I9zD3HhAEBHp7kFu73FETZTj5A8.png',
  },
  {
    slug: 'raven-claw',
    title: 'Raven Claw',
    date: 'June 2, 2024',
    excerpt: 'Minimal design isn\'t emptiness—it\'s clarity, intention, and the reduction of noise to amplify what truly matters.',
    mainImg: 'https://framerusercontent.com/images/3E2J9orCMTWzLWz1Wycx5lBwyAo.png',
    authorImg: 'https://framerusercontent.com/images/gykz5t3Atm9UtfI5YcnYcpGmng.png',
  },
  {
    slug: 'clay-nicolas',
    title: 'Clay Nicolas',
    date: 'June 10, 2025',
    excerpt: 'Portfolios today must be more than archives—they need to feel alive, intentional, and editorial by design.',
    mainImg: 'https://framerusercontent.com/images/FtLqkZe3KwZWnfRLOTtcw7gbCMg.png',
    authorImg: 'https://framerusercontent.com/images/PbYC4rG1Wy23pyEryQ1m4f1MivU.png',
  },
];

function ArticleCard({ article, delay }: { article: typeof articles[0]; delay: number }) {
  const ref = useRef<HTMLAnchorElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <a
      ref={ref}
      href={`/article/${article.slug}`}
      className={`block group transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
        {/* Left: main image */}
        <div className="overflow-hidden" style={{ height: 280 }}>
          <img
            src={article.mainImg}
            alt={article.title}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        {/* Right: content */}
        <div className="bg-black border border-white/10 border-l-0 p-8 flex flex-col justify-between">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 overflow-hidden flex-shrink-0">
              <img src={article.authorImg} alt={article.title} className="w-full h-full object-cover object-top" />
            </div>
            <h3
              className="text-white font-semibold leading-tight"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 'clamp(24px, 2.5vw, 36px)',
                letterSpacing: '-0.02em',
              }}
            >
              {article.title}
            </h3>
          </div>
          <div>
            <p className="text-white/40 text-xs mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>{article.date}</p>
            <p className="text-white/50 text-xs leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{article.excerpt}</p>
          </div>
        </div>
      </div>
    </a>
  );
}

export default function PalmerBlog() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-black py-20">
      <PalmerSectionLabel
        left1="© Visual Journal ジャーナル"
        left2="(WDX® — 10)"
        right="Creative Notes"
      />

      <div className="px-6">
        <div className="overflow-hidden mb-6">
          <h1
            className={`text-white font-semibold transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 'clamp(48px, 7vw, 100px)',
              letterSpacing: '-0.03em',
              lineHeight: 1,
            }}
          >
            Featured Article©
          </h1>
        </div>

        <div className="h-px bg-white/10 mb-8"></div>

        <div className="flex flex-col gap-0">
          {articles.map((article, i) => (
            <div key={article.slug} className="border-b border-white/10">
              <ArticleCard article={article} delay={i * 80} />
            </div>
          ))}
        </div>

        <div className="h-px bg-white/10 mt-0"></div>
      </div>
    </section>
  );
}
